import React from 'react';
import { useSelector } from 'react-redux';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { ErrorState } from '../../../components/ui/ErrorState';
import { PendingButton } from '../../../components/ui/PendingButton';
import { selectTasksClock } from '../../../features/operatorTasks/operatorTasksSlice';
import { updatedAgoText } from '../../../features/operatorTasks/operatorTasksUtils';

/** How old the list on screen is, by the local clock, and «Actualizar», as Inicio says it of its tray. */
const QueueFreshness = ({ receivedAt, onRefresh, refreshThunk, testId }) => {
  const clock = useSelector(selectTasksClock);
  const updated = updatedAgoText(receivedAt, clock);
  return (
    <div className="flex items-center gap-3">
      {updated && (
        <p data-testid={`${testId}-updated-at`} className="text-sm text-text-secondary">
          {updated}
        </p>
      )}
      <PendingButton
        pendingOn={refreshThunk}
        variant="ghost"
        size="lg"
        icon={RefreshCw}
        loadingLabel="Actualizando…"
        onClick={onRefresh}
        className="ml-auto"
        data-testid={`${testId}-refresh`}
      >
        Actualizar
      </PendingButton>
    </div>
  );
};

/**
 * A queue's states around its cards: loading the first page, failing, empty,
 * and more to load. Given `onRefresh`, it also says how old the list is and
 * offers «Actualizar»: the queues are asked again on their own only while the
 * operator is still (see `operatorRefresh`).
 *
 * @param {object} props
 * @param {{items: Array, loading: boolean, error: object|null, hasMore: boolean, receivedAt?: number|null}} props.queue
 * @param {() => void} props.onRetry
 * @param {() => void} props.onMore
 * @param {() => void} [props.onRefresh] - Asks the first page again.
 * @param {Function} [props.refreshThunk] - The thunk `onRefresh` sends, for its button to show it running.
 * @param {string} props.emptyText
 * @param {string} props.testId - The list's; `-empty`, `-error`, `-more`, `-updated-at` and `-refresh` hang from it.
 * @param {React.ReactNode} props.children - The cards.
 */
export const QueueList = ({ queue, onRetry, onMore, onRefresh, refreshThunk, emptyText, testId, children }) => {
  const firstLoad = queue.loading && queue.items.length === 0;

  if (firstLoad) {
    return (
      <div className="flex justify-center py-10" data-testid={`${testId}-loading`}>
        <Loader2 size={28} className="animate-spin text-ninja-green" />
      </div>
    );
  }
  if (queue.error && queue.items.length === 0) {
    return (
      <div data-testid={`${testId}-error`}>
        <ErrorState
          variant="compact"
          title="No pudimos cargar la lista"
          description={queue.error.message}
          onRetry={onRetry}
        />
      </div>
    );
  }

  const freshness = onRefresh ? (
    <QueueFreshness receivedAt={queue.receivedAt ?? null} onRefresh={onRefresh} refreshThunk={refreshThunk} testId={testId} />
  ) : null;

  if (queue.items.length === 0) {
    return (
      <div className="space-y-3">
        {freshness}
        <p data-testid={`${testId}-empty`} className="py-10 text-center text-text-secondary">
          {emptyText}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {freshness}
      <ul data-testid={testId} className="space-y-3">
        {children}
      </ul>
      {queue.hasMore && (
        <Button
          variant="outline"
          size="lg"
          fullWidth
          loading={queue.loading}
          loadingLabel="Cargando…"
          onClick={onMore}
          data-testid={`${testId}-more`}
        >
          Cargar más
        </Button>
      )}
    </div>
  );
};
